import React, { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { drawBackground, setupCanvas } from './senseCapChartDraw';

type ChartCanvasProps = {
    height?: number;
    minDrawWidth?: number;
    draw: (ctx: CanvasRenderingContext2D, size: { width: number; height: number }) => void;
};

const ChartCanvas = ({ height = 240, minDrawWidth, draw }: ChartCanvasProps) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const paint = useCallback(() => {
        const wrap = wrapRef.current;
        const canvas = canvasRef.current;
        if (!wrap || !canvas) return;
        const width = Math.max(wrap.clientWidth || 1, minDrawWidth ?? 0);
        const ctx = setupCanvas(canvas, width, height);
        if (!ctx) return;
        drawBackground(ctx, width, height);
        draw(ctx, { width, height });
    }, [draw, height, minDrawWidth]);

    useEffect(() => {
        paint();
        const node = wrapRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return undefined;
        let raf = 0;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(paint);
        });
        ro.observe(node);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [paint]);

    return (
        <Box
            ref={wrapRef}
            sx={{
                width: '100%',
                height,
                bgcolor: '#0c0e12',
                overflowX: minDrawWidth ? 'auto' : 'hidden',
                overflowY: 'hidden',
                borderRadius: 1,
            }}
        >
            <canvas ref={canvasRef} />
        </Box>
    );
};

export default ChartCanvas;
